import MovexGeo from "@movexgeo/react-native-movex";

/**
 * Mirrors the `{ coords: { latitude, longitude, accuracy } }` shape
 * that react-native-geolocation-service returned, so existing callsites
 * can switch with minimal churn — only the call style (callback ->
 * promise) and import change.
 */
export type Position = {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
};

export type GetCurrentLocationOptions = {
  /** Default `true` — same as the old enableHighAccuracy. */
  highAccuracy?: boolean;
  /** Default 10000 ms — matches movex.geo's PositionOptions default. */
  timeoutMs?: number;
};

/**
 * One-shot, accuracy-critical location capture. Backed by
 * `MovexGeo.captureCurrentPosition` (added in movex.geo 0.1.5),
 * which does NOT require the SDK to be initialized — safe to call
 * pre-login (e.g. onboarding, hub selection, address screens).
 *
 * Caller must hold `ACCESS_FINE_LOCATION`; the underlying call
 * rejects with `MovexGeoError { code: 'permission_denied' }`
 * otherwise.
 *
 * Android only. iOS currently rejects with `not_implemented`;
 * partner-db is Android-only deployment so callsites don't branch.
 */
export async function getCurrentLocation(
  opts: GetCurrentLocationOptions = {},
): Promise<Position> {
  const fix = await MovexGeo.captureCurrentPosition({
    enable_high_accuracy: opts.highAccuracy ?? true,
    timeout_ms: opts.timeoutMs ?? 10000,
  });
  return {
    coords: {
      latitude: fix.lat,
      longitude: fix.lng,
      accuracy: fix.accuracy_m,
    },
  };
}
